/**
 * O QUE A NAVEGAÇÃO LEMBRA DE CADA PESSOA, e o que ela deliberadamente esquece.
 *
 * O desenho da navegação lateral (entrega do dono, `sidebar.zip`) tem cinco
 * coisas que variam por quem usa: os favoritos, os recentes, o alfinete do
 * painel, "abrir ao passar o mouse" e "rótulos no menu".
 *
 * OS RECENTES FICARAM DE FORA, por decisão do dono em 26/10/2026 ("não coloque
 * recentes"). Eles não estão só escondidos: a lista não é guardada, o histórico
 * não é escrito e não há o que vazar para quem usar o mesmo computador depois.
 * Guardar um dado que a tela não mostra é a pior das duas opções.
 *
 * ## Por que no aparelho, e não no banco
 *
 * Nenhuma delas é dado da operação. Favorito não vira relatório, recente não
 * entra em auditoria, e o alfinete é preferência de quem está sentado naquele
 * computador — a mesma pessoa, num tablet, quer o painel solto. Guardar isso
 * no servidor criaria uma tabela, uma rota, uma migração e uma política de RLS
 * para uma informação que ninguém confere e que não dói perder.
 *
 * A consequência está escrita porque é real: TROCANDO DE COMPUTADOR, os
 * favoritos não vão junto. É o preço, e ele é pequeno — refazer um favorito é
 * um toque.
 *
 * ## Por pessoa, e não por navegador
 *
 * A chave leva o id da pessoa da sessão. Sem isso, duas pessoas que usam o
 * mesmo computador — que é o caso do computador do almoxarifado — herdariam os
 * favoritos uma da outra, e os "recentes" de quem saiu contariam a quem entrou
 * onde ela esteve. Isso não é vazamento de dado (são só nomes de tela), mas é
 * uma tela mentindo sobre quem fez o quê.
 *
 * ## Toda leitura e toda escrita dentro de try/catch
 *
 * `localStorage` lança em janela anônima com dados de site bloqueados, e volta
 * vazio depois de o navegador limpar o site. A navegação tem de desenhar do
 * mesmo jeito nos dois casos: o que não se lê vira o padrão, e o que não se
 * grava simplesmente não é lembrado na próxima vez.
 */

export interface PreferenciasDaNavegacao {
  /** Ids de tela (`comb.lancar`) que a pessoa marcou com a estrela. */
  readonly favoritos: readonly string[];
  /** O painel encostado no trilho, empurrando o conteúdo, em vez de flutuar. */
  readonly fixado: boolean;
  /** O painel abre ao passar o mouse no módulo, sem clicar. */
  readonly abrirAoPassar: boolean;
  /** O nome de cada módulo aparece embaixo do ícone, no trilho. */
  readonly rotulos: boolean;
}

export const PREFERENCIAS_PADRAO: PreferenciasDaNavegacao = Object.freeze({
  favoritos: [],
  /* SOLTO por padrão, e isto é decisão: o painel fixado come 288 px de
     largura da tela, e quem abre o sistema pela primeira vez não pediu isso.
     Quem o quer fixado fixa uma vez, e fica. */
  fixado: false,
  /* DESLIGADO por padrão: o painel abrindo sozinho ao passar o mouse
     atrapalha quem está indo para outro lugar da tela, e o desenho também o
     entrega desligado. */
  abrirAoPassar: false,
  /* LIGADO por padrão: ícone sem nome é charada para quem usa o sistema uma
     vez por semana, e o manual (p.19) nomeia conceitos, não telas. */
  rotulos: true,
});

const PREFIXO = 'ct-frotas:navegacao';

/** A chave desta pessoa. Sem sessão, uma chave própria — não a de ninguém. */
export function chaveDasPreferencias(usuarioId: string | null): string {
  return `${PREFIXO}:${usuarioId ?? 'sem-sessao'}`;
}

function textoOuNulo(valor: unknown): string | null {
  return typeof valor === 'string' && valor !== '' ? valor : null;
}

/**
 * Lê o que foi guardado, campo a campo, descartando o que não reconhece.
 *
 * NADA É CORRIGIDO NO CHUTE: valor de tipo errado vira o padrão daquele campo,
 * e não o padrão inteiro — uma lista de favoritos corrompida não pode apagar a
 * escolha do alfinete, que está ao lado dela e é independente.
 */
export function lerPreferencias(bruto: string | null): PreferenciasDaNavegacao {
  if (bruto === null) return PREFERENCIAS_PADRAO;
  let lido: unknown;
  try {
    lido = JSON.parse(bruto);
  } catch {
    return PREFERENCIAS_PADRAO;
  }
  if (typeof lido !== 'object' || lido === null) return PREFERENCIAS_PADRAO;
  const r = lido as Record<string, unknown>;
  const lista = (valor: unknown, teto: number): readonly string[] =>
    Array.isArray(valor) ? [...new Set(valor.map(textoOuNulo).filter((v): v is string => v !== null))].slice(0, teto) : [];
  const sim = (valor: unknown, padrao: boolean): boolean => (typeof valor === 'boolean' ? valor : padrao);
  return {
    /* Teto nos favoritos também: uma lista sem fim viraria uma segunda
       navegação dentro da navegação, e o desenho a desenha em uma tela. */
    favoritos: lista(r['favoritos'], 24),
    fixado: sim(r['fixado'], PREFERENCIAS_PADRAO.fixado),
    abrirAoPassar: sim(r['abrirAoPassar'], PREFERENCIAS_PADRAO.abrirAoPassar),
    rotulos: sim(r['rotulos'], PREFERENCIAS_PADRAO.rotulos),
  };
}

/** Liga ou desliga o favorito, preservando a ordem em que foram marcados. */
export function alternarFavorito(preferencias: PreferenciasDaNavegacao, id: string): PreferenciasDaNavegacao {
  const tem = preferencias.favoritos.includes(id);
  return {
    ...preferencias,
    favoritos: tem ? preferencias.favoritos.filter((f) => f !== id) : [...preferencias.favoritos, id],
  };
}

/**
 * Casa o texto digitado com uma tela, para o `Ctrl K`.
 *
 * Compara SEM acento e sem caixa, nos dois lados: quem digita "operacao" num
 * teclado sem acento agudo tem de achar "Operação", senão a busca responde
 * "nada encontrado" sobre uma tela que está ali.
 */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();
}

/** A tela casa com a busca? Confere o nome da tela, o do módulo e a descrição. */
export function casaComABusca(busca: string, partes: readonly string[]): boolean {
  const termos = normalizar(busca).split(/\s+/).filter((t) => t !== '');
  if (termos.length === 0) return true;
  const alvo = partes.map(normalizar).join(' ');
  /* TODOS os termos, e não qualquer um: "lancar combustivel" deve achar o
     lançamento do combustível, e não toda tela que tem uma das duas palavras. */
  return termos.every((t) => alvo.includes(t));
}
